import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/free-mode";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { OVERLAY_STYLES } from "../../styles/overlayStyles";

const swiperStyles = `
  .main-swiper .swiper-button-next,
  .main-swiper .swiper-button-prev {
    color: #010101 !important;
    font-size: 1em !important;
    transform: scale(0.6);
  }

  .main-swiper .swiper-button-prev {
    left: 10px !important;
  }
  .main-swiper .swiper-button-next {
    right: 10px !important;
  }

  .thumb-swiper {
    padding: 10px 0;
  }

  .thumb-swiper .swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .thumb-swiper .swiper-wrapper {
    display: flex;
    justify-content: center;
  }
`;

interface ImageCarouselProps {
  images: string[];
  productName: string;
  productStock: number;
  onImageClick: () => void;
  selectedImage: number;
  onImageSelect: (index: number) => void;
  onNextImage: () => void;
  onPrevImage: () => void;
}

export default function ImageCarousel({
  images,
  productName,
  productStock,
  onImageClick,
  selectedImage,
  onImageSelect,
  onNextImage,
  onPrevImage,
}: ImageCarouselProps) {
  const [mainSwiper, setMainSwiper] = useState<any>(null);
  const [thumbSwiper, setThumbSwiper] = useState<any>(null);

  useEffect(() => {
    if (mainSwiper && mainSwiper.slideTo) {
      mainSwiper.slideTo(selectedImage);
    }
  }, [selectedImage, mainSwiper]);

  const renderStockOrDiscountOverlay = () => {
    if (productStock > 0) {
      return (
        <></>
        /*{<Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundColor: '#dab99a',
            color: 'white',
            p: 1,
            py: 0.7,
            zIndex: 10,
            width: '130px'
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: '14px',
              fontFamily: "'Rubik', sans-serif",
            }}
          >
            10% OFF
          </Typography>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: '10px',
              fontFamily: "'Rubik', sans-serif",
              textTransform: "uppercase",
            }}
          >
            comprando 1 o más
          </Typography>
        </Box>*/
      );
    } else {
      return (
        <Box sx={OVERLAY_STYLES.stock}>
          <Typography sx={TYPOGRAPHY_STYLES.rubikMedium}>
            SIN STOCK
          </Typography>
        </Box>
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <style>{swiperStyles}</style>

      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          width: '100%',
        }}
      >
        {renderStockOrDiscountOverlay()}
        
        {/* Image count overlay */}
          <Box sx={OVERLAY_STYLES.imageCounter}>
            <Typography sx={TYPOGRAPHY_STYLES.rubikSmall}>
              {selectedImage + 1} / {images.length}
            </Typography>
          </Box>

        {/* Swiper Container for main image */}
        <Box
          sx={{
            position: "relative",
            width: '100%',
          }}
        >
          {/* Main Swiper Component */}
          <Swiper
            onSwiper={setMainSwiper}
            spaceBetween={10}
            navigation={true}
            modules={[Navigation]}
            className="main-swiper"
            onSlideChange={(swiper: any) => {
              onImageSelect(swiper.activeIndex);
            }}
            style={{
              width: '100%',
              height: 'auto'
            }}
          >
            {images.map((image, index) => (
              <SwiperSlide key={index} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box
                      sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                      }}
                  >
                      <Box
                          fetchPriority={'high'}
                          component="img"
                          src={image}
                          srcSet={`
                            ${image.replace("1024-1024", "480-0")} 480w,
                            ${image.replace("1024-1024", "640-0")} 640w,
                            ${image} 1024w,
                          `}
                          alt={`${productName} ${index + 1}`}
                          onClick={onImageClick}
                          sx={{
                              width: "100%",
                              height: "auto",
                              maxWidth: "1024px",
                              maxHeight: { xs: '600px', md: '1000px' },
                              imageRendering: "auto",
                              fontSmoothing: "antialiased",
                              WebkitBackfaceVisibility: "hidden",
                              transform: "translateZ(0)",
                              aspectRatio: 'auto 1024 / 1365',
                              objectFit: "contain",
                              cursor: "pointer",
                          }}
                      />
                  </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Box>

      {/* Thumbnail Navigation - Centered below main image */}
      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}>
        <Box sx={{
          width: '100%',
          maxWidth: { xs: '300px', md: '450px', lg: '750px' },
        }}>
          <Swiper
            onSwiper={setThumbSwiper}
            spaceBetween={0}
            slidesPerView="auto"
            freeMode={true}
            watchSlidesProgress={true}
            modules={[FreeMode]}
            className="thumb-swiper"
            centeredSlides={true}
            centeredSlidesBounds={true}
            breakpoints={{
              640: {
                slidesPerView: 5,
              },
              768: {
                slidesPerView: 6,
              },
              1024: {
                slidesPerView: 7,
              },
            }}
          >
            {images.map((image, index) => (
              <SwiperSlide 
                key={index} 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  width: 'auto'
                }}
                onClick={() => onImageSelect(index)}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`${productName} thumbnail ${index + 1}`}
                  sx={{
                    width: '100px',
                    height: '133.3px',
                    objectFit: "cover",
                    opacity: 0.7,
                    transition: "opacity 0.2s ease",
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Box>
    </Box>
  );
}














